import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { submissionApis } from "../../../api/submissionApis";

export default function ImportExcelModal({ open, onClose, reportId, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setOverwrite(false);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (importing || downloading) return;
    reset();
    onClose?.();
  };

  const handleDownloadTemplate = async () => {
    if (!reportId) return;
    setDownloading(true);
    setError("");
    try {
      await submissionApis.downloadDatapointExcelTemplate(reportId);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not download the Excel template."
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!reportId || !file) return;
    setImporting(true);
    setError("");
    try {
      const result = await submissionApis.importDatapointExcel(reportId, file, {
        overwrite,
      });
      onImported?.(result);
      reset();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Import failed. Please check the workbook and try again."
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UploadFileOutlinedIcon color="primary" />
        Import Excel
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download a template with this report&apos;s KPI input fields, fill the{" "}
          <strong>value</strong> column, then upload the workbook. Blank values
          are skipped. Matching uses <code>kpi_code</code> and{" "}
          <code>input_key</code>.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={
              downloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadOutlinedIcon />
              )
            }
            onClick={handleDownloadTemplate}
            disabled={downloading || importing || !reportId}
            sx={{ alignSelf: "flex-start" }}
          >
            {downloading ? "Downloading…" : "Download template"}
          </Button>

          <Box>
            <Button
              variant="outlined"
              component="label"
              disabled={importing}
              sx={{ mr: 1 }}
            >
              Choose .xlsx file
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const next = e.target.files?.[0] || null;
                  setFile(next);
                  setError("");
                }}
              />
            </Button>
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {file ? file.name : "No file selected"}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={importing}
              />
            }
            label="Overwrite existing KPI values for matching schemas"
          />
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={importing || downloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing || downloading || !file}
          startIcon={
            importing ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {importing ? "Importing…" : "Import data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
